
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { addToCart, getCart, type CartItem } from '../../utils/cart';
import type { Product } from '../../types/product';

interface QuickViewPanelProps {
  productId: string | null;
  onClose: () => void;
}

export default function QuickViewPanel({ productId, onClose }: QuickViewPanelProps) {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  const fetchProduct = useCallback(async (id: string) => {
    setLoading(true);
    setErrorMsg('');
    setQuantity(1);
    setAddedToCart(false);
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      setProduct(data);
    } catch {
      setErrorMsg('Impossible de charger le produit.');
      setProduct(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (productId) {
      fetchProduct(productId);
      // Small delay for animation
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => {
        setProduct(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [productId, fetchProduct]);

  useEffect(() => {
    if (productId) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [productId]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const handleAddToCart = () => {
    if (!product) return;
    const result = addToCart(
      {
        id: parseInt(product.id),
        name: product.name,
        price: product.price,
        image: product.image,
      },
      quantity,
      product.stock_quantity
    );
    if (result.success) {
      setAddedToCart(true);
      setTimeout(() => setAddedToCart(false), 3000);
    } else {
      setErrorMsg(result.message || 'Erreur');
      setTimeout(() => setErrorMsg(''), 3000);
    }
  };

  const handleBuyNow = () => {
    if (!product) return;
    const result = addToCart(
      {
        id: parseInt(product.id),
        name: product.name,
        price: product.price,
        image: product.image,
      },
      quantity,
      product.stock_quantity
    );
    if (result.success) {
      handleClose();
      setTimeout(() => navigate('/payment'), 350);
    } else {
      setErrorMsg(result.message || 'Erreur');
      setTimeout(() => setErrorMsg(''), 3000);
    }
  };

  const isSoldOut = product?.stock_quantity === 0;
  const isLowStock = product && product.stock_quantity > 0 && product.stock_quantity <= 5;

  const renderStars = (rating: number) => {
    const stars = [];
    const full = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;
    for (let i = 0; i < full; i++) {
      stars.push(<i key={`f${i}`} className="ri-star-fill text-[#2E7D32] text-xs"></i>);
    }
    if (hasHalf) stars.push(<i key="h" className="ri-star-half-fill text-[#2E7D32] text-xs"></i>);
    const rem = 5 - full - (hasHalf ? 1 : 0);
    for (let i = 0; i < rem; i++) {
      stars.push(<i key={`e${i}`} className="ri-star-line text-gray-300 text-xs"></i>);
    }
    return stars;
  };

  if (!productId && !isVisible) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleClose}
      />

      {/* Panel */}
      <div
        className={`fixed inset-y-0 right-0 w-full sm:w-[480px] bg-white z-[80] shadow-2xl transition-transform duration-300 ease-out flex flex-col ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-eye-line text-[#2E7D32]"></i>
            </div>
            <h2 className="text-sm font-semibold text-gray-900 tracking-wide">Aperçu rapide</h2>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 space-y-4 animate-pulse">
              <div className="w-full h-64 bg-gray-100 rounded-xl"></div>
              <div className="h-6 bg-gray-100 rounded w-3/4"></div>
              <div className="h-4 bg-gray-100 rounded w-1/2"></div>
              <div className="h-4 bg-gray-100 rounded w-full"></div>
              <div className="h-4 bg-gray-100 rounded w-full"></div>
              <div className="h-12 bg-gray-100 rounded"></div>
            </div>
          ) : product ? (
            <div className="p-6">
              {/* Image */}
              <div className="relative w-full h-72 sm:h-80 rounded-xl overflow-hidden bg-[#F1F8E9] mb-5">
                {product.status && product.status !== 'IN STOCK' && (
                  <span className="absolute top-3 left-3 z-10 px-3 py-1 bg-[#2E7D32] text-white text-[10px] font-bold tracking-wider rounded-full">
                    {product.status}
                  </span>
                )}
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Category */}
              <p className="text-[10px] text-gray-400 tracking-[0.25em] uppercase mb-1.5">{product.category}</p>

              {/* Name */}
              <h3 className="text-xl font-serif text-gray-900 mb-2 leading-snug">{product.name}</h3>

              {/* Rating */}
              <div className="flex items-center gap-2 mb-3">
                <div className="flex items-center gap-0.5">{renderStars(product.rating)}</div>
                <span className="text-xs font-medium text-gray-700">{product.rating}</span>
                <span className="text-xs text-gray-300">•</span>
                <span className="text-xs text-gray-400">{product.review_count} avis</span>
              </div>

              {/* Price */}
              <p className="text-2xl font-serif text-gray-900 mb-4">${product.price.toFixed(2)}</p>

              {/* Stock */}
              <div className="mb-4">
                {isSoldOut ? (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
                    <i className="ri-close-circle-line text-red-500 text-sm"></i>
                    <span className="text-xs font-semibold text-red-700">RUPTURE DE STOCK</span>
                  </div>
                ) : isLowStock ? (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg">
                    <i className="ri-error-warning-line text-orange-500 text-sm"></i>
                    <span className="text-xs font-semibold text-orange-700">
                      Plus que {product.stock_quantity} en stock
                    </span>
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <i className="ri-checkbox-circle-line text-emerald-500 text-sm"></i>
                    <span className="text-xs font-semibold text-emerald-700">En stock</span>
                  </div>
                )}
              </div>

              {/* Description */}
              <p className="text-sm text-gray-600 leading-relaxed mb-5 line-clamp-3">
                {product.long_description || product.description}
              </p>

              {/* Size */}
              <div className="flex items-center gap-2 mb-5">
                <span className="text-[10px] font-semibold tracking-wider text-gray-400 uppercase">Taille :</span>
                <span className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-800">{product.size}</span>
              </div>

              {/* Quantity */}
              {!isSoldOut && (
                <div className="mb-5">
                  <span className="text-[10px] font-semibold tracking-wider text-gray-400 uppercase block mb-2">Quantité</span>
                  <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden w-fit">
                    <button
                      onClick={() => quantity > 1 && setQuantity(quantity - 1)}
                      disabled={quantity <= 1}
                      className="w-10 h-10 flex items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <i className="ri-subtract-line text-sm"></i>
                    </button>
                    <span className="w-12 h-10 flex items-center justify-center text-sm font-semibold border-x border-gray-200">
                      {quantity}
                    </span>
                    <button
                      onClick={() => quantity < product.stock_quantity && setQuantity(quantity + 1)}
                      disabled={quantity >= product.stock_quantity}
                      className="w-10 h-10 flex items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <i className="ri-add-line text-sm"></i>
                    </button>
                  </div>
                </div>
              )}

              {/* Error message */}
              {errorMsg && (
                <div className="mb-4 px-4 py-2.5 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-500 text-sm"></i>
                  <span className="text-xs font-medium text-red-700">{errorMsg}</span>
                </div>
              )}

              {/* Success message */}
              {addedToCart && (
                <div className="mb-4 px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2">
                  <i className="ri-check-line text-emerald-500 text-sm"></i>
                  <span className="text-xs font-medium text-emerald-700">Ajouté au panier avec succès !</span>
                </div>
              )}

              {/* Shipping info */}
              <div className="mb-5 p-3.5 bg-[#F1F8E9] rounded-lg border border-[#C8E6C9]">
                <div className="flex items-center gap-2 mb-1.5">
                  <i className="ri-truck-line text-[#2E7D32] text-sm"></i>
                  <span className="text-xs font-semibold text-[#2E7D32]">Livraison</span>
                </div>
                <p className="text-xs text-gray-600">
                  {product.price >= 200
                    ? 'Livraison standard gratuite • 5–8 jours ouvrables'
                    : 'Standard à partir de $4.99 • Gratuite dès $200'}
                </p>
              </div>

              {/* Trust badges */}
              <div className="flex items-center gap-4 mb-2">
                <div className="flex items-center gap-1.5 text-gray-400">
                  <i className="ri-shield-check-line text-sm"></i>
                  <span className="text-[10px]">Paiement sécurisé</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-400">
                  <i className="ri-leaf-line text-sm"></i>
                  <span className="text-[10px]">100% Naturel</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-400">
                  <i className="ri-refresh-line text-sm"></i>
                  <span className="text-[10px]">Retour 30 jours</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6 text-center pt-20">
              <i className="ri-error-warning-line text-4xl text-gray-200 mb-3 block"></i>
              <p className="text-sm text-gray-400">Produit introuvable</p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {product && !loading && (
          <div className="flex-shrink-0 border-t border-gray-100 p-5 bg-white">
            {isSoldOut ? (
              <div className="text-center py-2">
                <p className="text-sm text-gray-400 font-medium">Ce produit est actuellement indisponible</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex gap-3">
                  <button
                    onClick={handleAddToCart}
                    className="flex-1 py-3 border-2 border-[#2E7D32] text-[#2E7D32] text-sm font-semibold rounded-lg hover:bg-[#E8F5E9] transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <i className="ri-shopping-cart-line"></i>
                    AJOUTER AU PANIER
                  </button>
                  <button
                    onClick={handleBuyNow}
                    className="flex-1 py-3 bg-[#2E7D32] text-white text-sm font-semibold rounded-lg hover:bg-[#1B5E20] transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <i className="ri-flashlight-line"></i>
                    ACHETER
                  </button>
                </div>
                <Link
                  to={`/product/${product.id}`}
                  onClick={handleClose}
                  className="block w-full py-2.5 text-center text-xs font-medium text-gray-500 hover:text-[#2E7D32] transition-colors cursor-pointer"
                >
                  Voir tous les détails du produit →
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
